<template>
    <div class="dekkingsgraad">
        <p>De huidige (beleids)dekkingsgraad van {{fonds ? fonds.fonds : ''}} is {{currentValue | formatNumber}}%.</p>
        <div class="dekkingsgraad__datapoint-wrapper">
            <div class="dekkingsgraad__datapoint" v-bind:class="dataPointClass">
                <div class="dekkingsgraad__historic-value">
                    {{getHistoricValue() | formatNumber}}%
                </div>
                <div class="dekkingsgraad__time-label">{{historicTimeLabel}}</div>
            </div>
        </div>
        <div class="dekkingsgraad__graph">
            <div class="dekkingsgraad__bar"
                 v-bind:class="getBarClass(i)"
                 v-bind:style="barWidth"
                 :index="i"
                 v-for="i in nmbSteps" :key="i">
                <div class="dekkingsgraad__bar-label"
                    v-if="showBarLabel(i)">{{getBarValue(i)}}</div>
            </div>
        </div>
        <div class="dekkingsgraad__controls" @click="playHandler">
            <div class="dekkingsgraad__button" v-bind:class="buttonClass"></div>
            <div class="dekkingsgraad__button-text" v-if="isPlaying">Aan het afspelen</div>
            <div class="dekkingsgraad__button-text" v-else>Toon de ontwikkeling van de afgelopen jaren</div>
        </div>
        <div class="dekkingsgraad__legend">
            <div class="dekkingsgraad__legend-item">
                <div class="dekkingsgraad__legend-marker dekkingsgraad__legend-marker--red"></div>
                <div class="dekkingsgraad__legend-item-label">Als de dekkingsgraad rood is, dan dreigen op termijn kortingen.</div>
            </div>
            <div class="dekkingsgraad__legend-item">
                <div class="dekkingsgraad__legend-marker dekkingsgraad__legend-marker--orange"></div>
                <div class="dekkingsgraad__legend-item-label">Bij oranje is er ruimte voor een beperkte indexatie.</div>
            </div>
            <div class="dekkingsgraad__legend-item">
                <div class="dekkingsgraad__legend-marker dekkingsgraad__legend-marker--green"></div>
                <div class="dekkingsgraad__legend-item-label">Bij groen is er ruimte voor een volledige indexatie.</div>
            </div>
        </div>

        <p>Indexatie is nodig om je koopkracht te behouden in perioden van inflatie. Bij inflatie stijgen de prijzen. Als de pensioenen niet stijgen met de inflatie dan ga je er financieel op achteruit.</p>

    </div>
</template>

<script>
    import { minBy, maxBy } from 'lodash';

    const redLimit = 104.2;
    const animationInterval = 2 * 1000;

    export default {
        name: "Dekkingsgraad",
        props: {
            fonds: Object
        },
        data() {
          return {
              nmbSteps: 0,
              min: 0,
              max: 0,
              step: 0,
              currentTimeIndex: -1,
              isPlaying: false
          }
        },
        watch: {
          fonds() {
              this.currentTimeIndex = this.fonds ? this.fonds.series.length - 1 : 0;
              this.initGraph();
          }
        },
        computed: {
          barWidth() {
              const width = Math.floor(1000 / this.nmbSteps) / 10;
              return {
                  width: `calc(${width}% - 2px)`
              }
          },
          currentValue() {
            return this.fonds && this.fonds.series.length > 0 ? this.fonds.series[this.fonds.series.length - 1].value : 'onbekend';

          },
          buttonClass() {
              return {
                  'dekkingsgraad__button--disabled': this.isPlaying
              };
          },
          dataPointClass() {
              const value = this.getHistoricValue();
              const scale = this.getColorScale(value);
              const elementClass = `dekkingsgraad__datapoint--${scale}`;
              const result = {};
              result[elementClass] = true;
              return result;
          },
          historicTimeLabel() {
              let label = 'onbekend';
              if (this.fonds && this.currentTimeIndex >= 0) {
                  const date = this.fonds.series[this.currentTimeIndex].date;
                  const year = date.getFullYear();
                  const monthIndex = date.getMonth();
                  const month = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'][monthIndex];
                  label = `${month} ${year}`;
              }
              return label
          }
        },
        methods: {
            initGraph() {
                const fonds = this.fonds;
                /* Calculate the number of steps. */
                let max = maxBy(fonds.series, 'value').value;
                let min = minBy(fonds.series, 'value').value;
                min = Math.floor(min/10) * 10;
                min = Math.min(min, 100);
                max = Math.max(max, fonds.vereisteDekkingsgraad * 1.1);
                let nmbSteps = 40;
                let step = (max - min) / nmbSteps;
                if (step < 1) {
                    step = 1;
                } else if (step < 2) {
                    step = 2;
                } else if (step < 5) {
                    step = 5;
                } else if (step < 10) {
                    step = 10;
                } else {
                    step = 25;
                }
                nmbSteps = Math.ceil((max - min) / step);
                /* The number of steps is dividable by 5 incremented by 1 */
                const remainder = nmbSteps % 5;
                nmbSteps += (6 - remainder) % 5;

                this.nmbSteps = nmbSteps;
                this.min = min;
                this.max = max;
                this.step = step;


            },
            getHistoricValue() {
                let value = 0;
                if (this.fonds && this.currentTimeIndex >= 0) {
                    value = this.fonds.series[this.currentTimeIndex].value;
                }
                return value;
            },
            getBarValue(index) {
                return this.min + (index - 1) * this.step;
            },
            showBarLabel(index) {
                return (index - 1) % 5 === 0;
            },
            getBarClass(index) {
                const value = this.getBarValue(index);
                const result = {
                    'dekkingsgraad__bar--active': value <= this.fonds.series[this.currentTimeIndex].value
                };
                const scale = this.getColorScale(value);
                result[`dekkingsgraad__bar--${scale}`] = true;

                return result;
            },
            getColorScale(value) {
                let scale;
                if (value < redLimit) {
                    scale = 'red';
                } else if (value < this.fonds.vereisteDekkingsgraad) {
                    scale = 'orange';
                } else {
                    scale = 'green';
                }
                return scale;
            },
            playHandler() {
                if (!this.isPlaying) {
                    this.isPlaying = true;
                    this.play();
                }
            },
            play() {
                const seriesLength = this.fonds.series.length;
                this.currentTimeIndex = (this.currentTimeIndex + 1) % seriesLength;
                if (this.currentTimeIndex < seriesLength - 1) {
                    setTimeout(() => {
                        this.play()
                    }, animationInterval);
                } else {
                    this.isPlaying = false;
                }

            }
        }
    }
</script>

<style lang="stylus">

    .dekkingsgraad__graph
        height 90px

    .dekkingsgraad__bar
        float left
        height 30px
        background-color $color-grey-background
        border 1px solid $color-grey-background
        min-width 1px
        margin-right 2px
        position relative
        top 10px
        overflow visible
        transition background-color 1s
        &:nth-child(5n + 1)
            top 0
            height 50px
        &.dekkingsgraad__bar--red
            border-color $color-red
            &.dekkingsgraad__bar--active
                background-color $color-red

        &.dekkingsgraad__bar--orange
            border-color $color-orange
            &.dekkingsgraad__bar--active
                background-color $color-orange
        &.dekkingsgraad__bar--green
            border-color $color-green
            &.dekkingsgraad__bar--active
                background-color $color-green

    .dekkingsgraad__bar-label
        position absolute
        top 60px
        color #66
        font-size .9em

    .dekkingsgraad__legend
        margin-bottom $gutter

    .dekkingsgraad__legend-item
        width 100%
        display flex
        align-items flex-start
        font-size .9em

    .dekkingsgraad__legend-marker
        display block
        margin-top 3px
        width .8em
        height 1em
        margin-right ($gutter/2)
        &.dekkingsgraad__legend-marker--red
            background-color $color-red
        &.dekkingsgraad__legend-marker--orange
            background-color $color-orange
        &.dekkingsgraad__legend-marker--green
            background-color $color-green

    .dekkingsgraad__legend-item-label
        width calc(100% - 2em)
        display block
        color $color-secondary-text

    .dekkingsgraad__datapoint-wrapper
        text-align center
        margin-top $gutter

    .dekkingsgraad__datapoint
        display inline-block
        border-radius 50%
        width 8em
        height @width
        padding-top 1.5em
        margin-bottom $gutter
        text-align center
        color #fff
        &.dekkingsgraad__datapoint--red
            background-color $color-red
        &.dekkingsgraad__datapoint--orange
            background-color $color-orange
        &.dekkingsgraad__datapoint--green
            background-color $color-green

    .dekkingsgraad__historic-value
        font-size 1.7em
        display inline-block
        border-bottom 1px solid #fff
        padding .4em 0

    .dekkingsgraad__time-label
        padding .4em 0

    .dekkingsgraad__controls
        text-align center
        margin-bottom $gutter
        cursor pointer

    .dekkingsgraad__button
        display inline-block
        background-color $color-primary
        background-image url('https://nos.nl/img/icons/icon-play.svg')
        background-position center
        background-repeat no-repeat
        position: relative;
        width: 1.33333em;
        height: 1.33333em;
        padding: .33333em;
        border-radius: 100%;
        background-size: .55556em;
        vertical-align: top;
        font-size 2em
        &.dekkingsgraad__button--disabled
            opacity .3

    .dekkingsgraad__button-text
        color $color-secondary-text
        font-size .9em
        margin-left $gutter
</style>
